import { Permission, userHasAnyPermission } from "~/domains/user/utils/permissions";
import { useDispatch, useSelector } from "react-redux";
import { selectWishlistAmount } from "~/features/wishlist/redux/wishlistSelectors";
import { useLoadWishlistsOnMount } from "~/features/wishlist/utils/wishlistHelper";
import { useProductComparisonCount } from "~/features/compareProducts/api/productComparisonQuery";
import { NotificationNavItem } from "~/features/navigation/components/Notifications/NotificationNavItem";
import { useTranslation } from "react-i18next";
import { canSeeWishlistButton } from "~/utils/features";
import { HeaderMinicart } from "~/features/cart/minicart/MinicartComponent";
import { NavItems } from "~/features/navigation/components/headerMenu/PersonalNavItems";
import { PersonalNavItem } from "~/features/navigation/components/headerMenu/PersonalNavItem";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CompareOutlinedIcon from "@mui/icons-material/CompareOutlined";
import { styled } from "@schaeffler-commerce/design-system";
import { RouterLink } from "~/common/components/Link";
import { useComparisonDialog } from "~/features/compareProducts/hooks/useComparisonDialog";
import {
  canSeeFCC,
  canSeeMyCompany,
  canSeeSalesFinder,
} from "~/features/navigation/components/headerMenu/navigationVisibility";
import { userIsPublic } from "~/domains/user/utils/roles";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";

export {
  canSeeFCC,
  canSeeMyCompany,
  canSeeSalesFinder,
} from "~/features/navigation/components/headerMenu/navigationVisibility";

const DesktopPersonalNav = () => {
  const { t } = useTranslation();
  const canSeeCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);
  const dispatch = useDispatch();

  const showWishlistMenuEntry = canSeeWishlistButton();

  const { count: listItems } = useProductComparisonCount();
  const { openComparisonDialog } = useComparisonDialog();

  useLoadWishlistsOnMount();

  const wishlistAmount = useSelector(selectWishlistAmount);

  const wishlistItem = (
    <PersonalNavItem
      name={t("wishlistPage.title")}
      icon={<BookmarkBorderOutlinedIcon />}
      badgeContent={wishlistAmount !== undefined ? wishlistAmount : ""}
    />
  );

  const onComparisonClick = () => {
    if (userIsPublic()) {
      dispatch(openAuthOverlay("login"));
      return;
    }
    openComparisonDialog();
  };

  const onClick = (e: React.MouseEvent) => {
    if (userIsPublic()) {
      e.preventDefault();
      dispatch(openAuthOverlay("login"));
    }
  };

  return (
    <PersonalNavRoot className="scha__personal-nav">
      <PersonalNavList className="scha__personal-nav__list">
        {canSeeFCC() && (
          <li className="scha__personal-nav__list__item">
            <NavItems.FCC />
          </li>
        )}
        {canSeeSalesFinder() && (
          <li className="scha__personal-nav__list__item">
            <RouterLink to="/store-finder" data-test-id={"header-sales-partner"}>
              <PersonalNavItem
                data-test-id={"header-btn-sales-partner"}
                icon={<LocationOnOutlinedIcon />}
                name={t("salesPartner.title")}
              />
            </RouterLink>
          </li>
        )}
        {canSeeMyCompany() && (
          <li className="scha__personal-nav__list__item">
            <NavItems.MyCompany />
          </li>
        )}
        <NotificationNavItem />
        <li className="scha__personal-nav__list__item">
          <PersonalNavItem
            icon={<CompareOutlinedIcon />}
            badgeContent={listItems}
            name={t("header.link.comparison.list")}
            onClick={onComparisonClick}
          />
        </li>
        {showWishlistMenuEntry && (
          <li className="scha__personal-nav__list__item">
            {userIsPublic() ? (
              <a aria-label={t("wishlistPage.title")} onClick={onClick}>
                {wishlistItem}
              </a>
            ) : (
              <RouterLink to="/wishlist" aria-label={t("wishlistPage.title")} onClick={onClick}>
                {wishlistItem}
              </RouterLink>
            )}
          </li>
        )}
        {canSeeCart && (
          <li className="scha__personal-nav__list__item">
            <HeaderMinicart className={"scha__personal-nav__list__item__cart"} />
          </li>
        )}
      </PersonalNavList>
    </PersonalNavRoot>
  );
};

export default DesktopPersonalNav;

const PersonalNavRoot = styled("div")({
  height: "100%",
  width: "100%",
});

const PersonalNavList = styled("ul")(({ theme }) => ({
  display: "flex",
  height: "inherit",
  justifyContent: "flex-end",
  gap: theme.spacing(0.5),
  paddingLeft: 0,
  "& > .scha__personal-nav__list__item": {
    display: "flex",
    alignItems: "center",
    listStyle: "none",
  },
}));
